import { formatPrice } from "@/utilities";
import { Flex, Spin, Tag } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const funnelTemplate = [
  { label: "customer", bg: "rgba(249, 240, 255, 1)", color: "rgba(114, 46, 209, 1)", translationKey: "quotes.status.Customer", displayLabel: undefined as string | undefined },
  { label: "follow", bg: "rgba(255, 247, 230, 1)", color: "rgba(250, 140, 22, 1)", translationKey: "quotes.status.Following", displayLabel: undefined as string | undefined },
  { label: "bought", bg: "rgba(233, 244, 239, 1)", color: "rgba(21, 116, 75, 1)", translationKey: "quotes.status.BoughtOnOff", displayLabel: "Comprado On/Off/Asistido" },
];

export const SpecialFunnel = ({
  list,
  loading,
}: {
  list: any[];
  loading: boolean;
}) => {
  const { t } = useTranslation();

  const normalizedList = useMemo(() => {
    const customer = list.find((i) => i.group === "customer");
    const customerStore = list.find((i) => i.group === "customer_store");
    const follow = list.find((i) => i.group === "follow");

    const mergedCustomer =
      customer || customerStore
        ? {
            number: (customer?.number || 0) + (customerStore?.number || 0),
            count: (customer?.count || 0) + (customerStore?.count || 0),
          }
        : null;

    return [
      mergedCustomer ? { ...funnelTemplate[0], ...mergedCustomer } : null,
      follow ? { ...funnelTemplate[1], number: follow.number, count: follow.count } : null,
    ].filter(Boolean) as Array<(typeof funnelTemplate)[0] & { number: number; count: number }>;
  }, [list]);

  const boughtItem = useMemo(() => {
    const buyOn = list.find((i) => i.group === "buy_on");
    const buyOff = list.find((i) => i.group === "buy_off");
    const boughtAssisted = list.find((i) => i.group === "bought_assisted");
    if (!buyOn && !buyOff && !boughtAssisted) return null;
    return {
      number: (buyOn?.number || 0) + (buyOff?.number || 0) + (boughtAssisted?.number || 0),
      count: (buyOn?.count || 0) + (buyOff?.count || 0) + (boughtAssisted?.count || 0),
    };
  }, [list]);

  const lostItem = list.find((i) => i.group === "lost");
  const archivedItem = list.find((i) => i.group === "archived");

  // Widths proportional to count; bottom of each segment = top of the next → no gaps in SVG
  const segments = useMemo(() => {
    const base = [...normalizedList];
    if (boughtItem) base.push({ ...funnelTemplate[2], ...boughtItem });
    if (base.length === 0) return [];

    const maxAmount = Math.max(...base.map((i) => i.number), 1);

    return base.map((item, index, arr) => {
      const topW = item.number / maxAmount;
      const botW =
        index < arr.length - 1
          ? arr[index + 1].number / maxAmount
          : Math.max(0.3, topW * 0.5);
      return { ...item, topW, botW };
    });
  }, [normalizedList, boughtItem]);

  const hasData =
    segments.length > 0 ||
    boughtItem != null ||
    (lostItem?.count || 0) > 0 ||
    (archivedItem?.count || 0) > 0;

  if (loading)
    return (
      <Flex align="center" justify="center" style={{ width: "100%", height: "100%" }}>
        <Spin spinning={loading} />
      </Flex>
    );

  if (!hasData)
    return (
      <div style={{ textAlign: "center", color: "rgba(0, 0, 0, 0.45)", paddingTop: 120 }}>
        No data.
      </div>
    );

  const n = segments.length;

  return (
    <div className="funnel" style={{ width: "100%", display: "flex", flexDirection: "column", height: 340 }}>
      {/* Single SVG — all trapezoids share the same coordinate space, no gaps */}
      {n > 0 && (
        <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ display: "block" }}
          >
            {segments.map((item, index) => {
              const y0 = (index / n) * 100;
              const y1 = ((index + 1) / n) * 100;
              const x1 = (0.5 - item.topW / 2) * 100;
              const x2 = (0.5 + item.topW / 2) * 100;
              const x3 = (0.5 + item.botW / 2) * 100;
              const x4 = (0.5 - item.botW / 2) * 100;
              return (
                <polygon
                  key={index}
                  points={`${x1},${y0} ${x2},${y0} ${x3},${y1} ${x4},${y1}`}
                  fill={item.bg}
                />
              );
            })}
          </svg>

          {/* Labels overlaid on each segment */}
          {segments.map((item, index) => (
            <div
              key={index}
              style={{
                position: "absolute",
                top: `${((index + 0.5) / n) * 100}%`,
                transform: "translateY(-50%)",
                width: "100%",
                textAlign: "center",
                color: item.color,
                pointerEvents: "none",
              }}
            >
              <div style={{ fontSize: 15, textTransform: "capitalize" }}>
                {item.displayLabel ?? t(item.translationKey)}
              </div>
              <div style={{ fontSize: 14 }}>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>{item.count} ×</span>{" "}
                <strong>{formatPrice(item.number)}</strong>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lost + Archived chips */}
      <Flex justify="space-between" style={{ marginTop: 16 }}>
        <div>
          <Tag
            style={{
              backgroundColor: "rgba(255, 241, 240, 1)",
              borderColor: "rgba(255, 163, 158, 1)",
              color: "rgba(245, 34, 45, 1)",
            }}
          >
            {t("quotes.tag.statuslost")}
          </Tag>
          <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>{lostItem?.count || 0} x </span>
          <strong>{formatPrice(lostItem?.number || 0)}</strong>
        </div>
        <div>
          <Tag
            style={{
              backgroundColor: "rgba(255, 255, 255, 1)",
              borderColor: "rgba(217, 217, 217, 1)",
              color: "rgba(0, 0, 0, 0.88)",
            }}
          >
            {t("quotes.tag.statusarchived")}
          </Tag>
          <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>{archivedItem?.count || 0} x </span>
          <strong>{formatPrice(archivedItem?.number || 0)}</strong>
        </div>
      </Flex>
    </div>
  );
};
